import { useState } from 'react'
import { createSearchParams, useParams } from 'react-router-dom'

import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceGenerateTemplateQuestionnaireUsingAiDocument,
  PerformanceReviewerStrategiesStrategyEnum,
  PerformanceTemplateEditPageDocument,
  UpdatePerformanceProcessTemplateQuestionnaireDefaultRatingScaleDocument,
  UpdatePerformanceProcessTemplateQuestionnaireDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { MissingInformation } from 'modules/performance_process/components/review/show/MissingInformation'
import {
  type HandleUpdateQuestionnaire,
  ReviewTypeQuestions,
} from 'modules/performance_process/components/review/show/ReviewTypeQuestions'
import { CustomTemplateQuestionnairePreviewSidepanel } from 'modules/performance_process/components/templates/CustomTemplateQuestionnairePreviewSidepanel'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'
import type { Scale } from 'modules/questionnaires/lib/types'

const TemplateEditPage = () => {
  const { templateId } = useParams()
  const { navigation } = useNavigation()
  const [previewSidepanelOpen, setPreviewSidepanelOpen] = useState(false)
  const { hasCustomScoreConfigurationEnabled } = useNewPerformanceModuleContext()

  const { data, error, loading } = useQuery(PerformanceTemplateEditPageDocument, {
    variables: {
      templateId: Number(templateId),
    },
  })

  const template = data?.performance.reviewProcessCustomTemplate
  const reviewerStrategies = template?.reviewerStrategies || []

  const settingsPath = (section: string) => ({
    pathname: navigation.performanceProcess.reviews.templates.template.settings.fullPath({
      templateId: Number(templateId),
    }),
    search: createSearchParams({ section: section }).toString(),
  })

  const [updateQuestionnaire, { loading: updatingQuestionnaire }] = useMutation(
    UpdatePerformanceProcessTemplateQuestionnaireDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.performanceMutations
          .updateQuestionnaireForStrategyPerformanceReviewProcessCustomTemplateQuestionnaireByStrategy,
    }
  )

  const [updateDefaultRatingScale, { loading: updatingDefaultRatingScale }] = useMutation(
    UpdatePerformanceProcessTemplateQuestionnaireDefaultRatingScaleDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.performanceMutations
          .updateDefaultRatingScalePerformanceReviewProcessCustomTemplateQuestionnaireByStrategy,
    }
  )

  const [generateTemplateQuestionnaireUsingAI] = useMutation(
    PerformanceGenerateTemplateQuestionnaireUsingAiDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.performanceMutations
          .parseAndUpdateQuestionnaireForStrategyPerformanceReviewProcessCustomTemplateQuestionnaireByStrategy,
    }
  )

  const handleQuestionnaireUpdate: HandleUpdateQuestionnaire = async (
    questionnaireContent,
    strategy
  ) => {
    if (!template) return { success: false }
    const response = await updateQuestionnaire({
      variables: {
        id: Number(template.id),
        questionnaireContent,
        strategy,
      },
    })
    const result = handleResult(response, () => {})
    return { success: result.success }
  }

  const handleDefaultRatingChange = async (scales: Scale[]) => {
    if (!template) return { success: false }
    const response = await updateDefaultRatingScale({
      variables: {
        defaultRatingScale: scales,
        id: template.id,
      },
    })

    const result = handleResult(response, () => {})
    return { success: result.success }
  }

  const handleSubmitGenerateUsingAI = async (
    content: string,
    strategy: PerformanceReviewerStrategiesStrategyEnum
  ) => {
    return generateTemplateQuestionnaireUsingAI({
      variables: {
        performanceCustomTemplateId: Number(template?.id),
        strategy,
        content,
      },
    })
  }

  return (
    <ResponsivePage.Body
      loading={loading}
      error={error || !template}
      authorized={template?._policies?.manage.can}
    >
      {template && (
        <>
          <Box gap="s40">
            {template.questionnaires && (
              <>
                <Box gap="s16">
                  <Text.Header2 color="grey1000">
                    {i18n.t('performance_process.reviews.questionnaire_configuration')}
                  </Text.Header2>
                  <>
                    {!reviewerStrategies.length ? (
                      <Box justifyContent="center" alignItems="center">
                        <Link.Router to={settingsPath('basic')}>
                          <MissingInformation
                            description={i18n.t(
                              'performance_process.reviews.missing_questions_general'
                            )}
                          />
                        </Link.Router>
                      </Box>
                    ) : (
                      <ReviewTypeQuestions
                        questionnaireableId={template.id}
                        questionnaireableType="review_process_custom_template"
                        reviewType={reviewerStrategies}
                        questionnaires={template.questionnaires}
                        defaultScoringRange={template.questionnaires.defaultRatingScale}
                        onUpdateQuestionnaire={handleQuestionnaireUpdate}
                        updatingQuestionnaire={updatingQuestionnaire}
                        handleSubmitRating={{
                          submitting: updatingDefaultRatingScale,
                          onSubmit: handleDefaultRatingChange,
                        }}
                        handleSubmitGenerateUsingAI={handleSubmitGenerateUsingAI}
                        generateQuestionsUsingAiEnabled={
                          template._policies?.generateReviewQuestionsUsingAi.can || false
                        }
                        scoreConfiguration={template.scoreConfiguration}
                        hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
                      />
                    )}
                  </>
                </Box>
              </>
            )}
          </Box>
          <CustomTemplateQuestionnairePreviewSidepanel
            isOpen={previewSidepanelOpen}
            onClose={() => setPreviewSidepanelOpen(false)}
          />
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default TemplateEditPage
